import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Dropdown } from 'components/components/atoms';
import LeftArrowIcon from 'components/icons/LeftArrow';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import cx from 'classnames';

const Container = styled.div`
  ${({ isMobile, fullWidth }) =>
    isMobile || fullWidth ? 'width: 100%;' : 'width: 7.875rem;'}
`;

const LeftIconLink = styled.div`
  position: absolute;
  display: flex;
  opacity: 0;
  align-items: center;
  right: 0;
  bottom: ${({ isState }) => (isState ? '0.25rem' : '0')};
  top: 0;
  transition: all 0.3s ease-out;
`;

const City = styled.div`
  ${tw`flex font-graphie leading-6 text-white font-light relative whitespace-nowrap`}
  &:hover {
    font-weight: bold;
  }
  &:hover ${LeftIconLink} {
    opacity: 1;
    right: -1.5rem;
  }
`;

const State = styled.button`
  ${tw`flex font-graphie leading-6 text-white font-light relative whitespace-nowrap`}
  &:hover {
    font-weight: bold;
  }
  &:hover ${LeftIconLink} {
    opacity: 1;
    right: -1.5rem;
  }
`;

const CityList = styled.div`
  display: inline-grid;
  column-gap: 5rem;
`;

const StateContainer = styled.div`
  ${({ isMobile }) =>
    !isMobile &&
    `max-width: 8rem;
  margin-left: 1.75rem;
  margin-right: 1.75rem;
  &:nth-child(1) {
    margin-left: 0rem;
  }`}
`;

const MarketDropdown = (props) => {
  const {
    selectedMetro,
    onMetroChange,
    metros,
    country = 'USA',
    outlined = false,
    fullWidth = false,
    dataMapped = false,
    stateSelectable = true,
    activeFilter,
    handleSelectFilter = () => {},
    withSelectClick = false,
    fullWidthMobile,
    active: activeProp,
    setActive: setActiveProp,
    dropdownDescription = '',
    isSeachFilter = false,
    withStateArrow = true,
    withMultipleSelection = false,
  } = props;

  const ref = useRef();
  const [activeState, setActiveState] = useState(false);

  const active = activeProp || activeState;
  const setActive = setActiveProp || setActiveState;

  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const isMobile = ['sm', 'xs'].includes(breakpoint);
  const filteredMetros =
    metros && !dataMapped
      ? metros.reduce((acc, city) => {
          if (city.country !== country) {
            return acc;
          }

          const cityExists = acc.find((item) => city.state === item.state);
          if (cityExists) {
            cityExists.cities = [...cityExists.cities, city];
            cityExists.cities.sort((a, b) =>
              a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1,
            );
          } else {
            acc.push({
              state: city.state,
              name: city.state,
              cities: [city],
              country: city.country,
            });
            acc.sort((a, b) =>
              a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1,
            );
          }
          return acc;
        }, [])
      : metros || [];

  const handleMetroClick = async (city, flag = false) => {
    await new Promise((resolve) => {
      onMetroChange(city, flag);
      resolve();
    });
    setTimeout(() => {
      setActive(false);
    }, 1000);
  };

  const title =
    selectedMetro && selectedMetro.length > 1
      ? selectedMetro[0].name + ', ' + selectedMetro[1].name
      : selectedMetro[0] && typeof selectedMetro[0] === 'object'
      ? selectedMetro[0].name
      : selectedMetro;

  return (
    <Container
      isMobile={isMobile}
      fullWidth={fullWidth}
      onBlur={() => {
        setTimeout(() => {
          if (ref && ref.current) {
            const containsFocusedElement = ref.current.contains(
              document.activeElement,
            );
            if (!containsFocusedElement) {
              setActive(false);
            }
          }
        }, 100);
      }}
      ref={ref}
    >
      <Dropdown
        label={title || 'Market'}
        active={active}
        setActive={setActive}
        outlined={outlined}
        textLightBlue
        activeFilter={activeFilter}
        onSelectFilterClick={handleSelectFilter}
        withSelectClick={true}
        fullWidthMobile={fullWidthMobile}
        dropdownName={'Markets'}
        dropdownDescription={
          isSeachFilter ? title + '. ' + dropdownDescription : ''
        }
      >
        <div className='md:flex'>
          <div className='md:flex items-start justify-between py-1 pl-2 pr-1'>
            {filteredMetros.map((state) => (
              <StateContainer
                key={state.state}
                className='mb-10 md:mb-0 flex flex-col item-start justify-start whitespace-nowrap'
                isMobile={isMobile}
              >
                <State
                  key={state.state}
                  onClick={() => stateSelectable && handleMetroClick(state)}
                  aria-label={state.state}
                  role={withMultipleSelection ? 'checkbox' : 'button'}
                  aria-checked={
                    withMultipleSelection
                      ? selectedMetro.find(
                          (e) =>
                            e.name.toLowerCase() === state.name.toLowerCase(),
                        )
                        ? true
                        : false
                      : false
                  }
                >
                  <div className='relative flex tracking-normal font-trade-gothic-20 tracking-wider text-base mb-1 text-white uppercase text-left'>
                    {withMultipleSelection && (
                      <div
                        className={cx(
                          'w-4 h-4 mr-2 rounded-full border-2 border-white',
                          {
                            'bg-white': selectedMetro.find(
                              (e) =>
                                e.name.toLowerCase() ===
                                state.name.toLowerCase(),
                            ),
                          },
                        )}
                      ></div>
                    )}
                    <h3>{state.state}</h3>
                    <LeftIconLink isState={true}>
                      {withStateArrow && (
                        <LeftArrowIcon
                          className={'transform text-aqua w-3 h-3 rotate-180'}
                          id={'MarketDropdown-' + state.state}
                        />
                      )}
                    </LeftIconLink>
                  </div>
                </State>
                <CityList>
                  {state.cities.map((city) => (
                    <City key={city.name}>
                      <div className='relative flex tracking-normal text-sm'>
                        {withMultipleSelection && (
                          <button
                            className={cx(
                              'w-4 h-4 mr-2 mt-1 rounded-full border-2 border-white',
                              {
                                'bg-white': selectedMetro.find(
                                  (e) =>
                                    e.name.toLowerCase() ===
                                      city.name.toLowerCase() ||
                                    e.name.toLowerCase() ===
                                      city.state.toLowerCase(),
                                ),
                              },
                            )}
                            onClick={() => handleMetroClick(city)}
                            role={withMultipleSelection ? 'checkbox' : 'button'}
                            aria-label={city.name}
                            aria-checked={
                              withMultipleSelection
                                ? selectedMetro.find((e) =>
                                    selectedMetro.find(
                                      (e) =>
                                        e.name.toLowerCase() ===
                                          city.name.toLowerCase() ||
                                        e.name.toLowerCase() ===
                                          city.state.toLowerCase(),
                                    ),
                                  )
                                  ? true
                                  : false
                                : false
                            }
                          ></button>
                        )}
                        <button
                          aria-label={city.name}
                          onClick={() => handleMetroClick(city, true)}
                        >
                          {' '}
                          {city.name.length > 15
                            ? city.name.substring(0, 15) + '...'
                            : city.name}
                        </button>

                        <LeftIconLink>
                          <LeftArrowIcon
                            className={'transform text-aqua w-3 h-3 rotate-180'}
                            id={'MarketDropdown-' + city.name}
                          />
                        </LeftIconLink>
                      </div>
                    </City>
                  ))}
                </CityList>
              </StateContainer>
            ))}
          </div>
        </div>
      </Dropdown>
    </Container>
  );
};

export default MarketDropdown;
